import { Button, FormRoot, RHF, zodResolver } from "@devhop/ui";
import { Save } from "lucide-react";
import { useEffect } from "react";
import { z } from "zod";
import type {
  CreateSOPMappingDTO,
  UpdateSOPMappingDTO,
} from "@/modules/sop/domain/contracts/mapping";
import { config } from "@/shared/lib/config";
import { fetcher } from "@/shared/lib/fetcher";
import type { LookupItem } from "@/shared/lib/utils";
import { fetchLookupForInfinite, hydrateLookupItem } from "@/shared/lib/utils";
import { FormInfiniteCombobox } from "@/shared/ui/FormInfiniteCombobox";

type SOPItem = LookupItem;
type PositionItem = LookupItem & {
  title?: string;
  departmentId?: string;
};
type DepartmentItem = LookupItem;

const SOPMappingFormSchema = z.object({
  sopId: z.string().min(1, "ກະລຸນາເລືອກ SOP"),
  positionId: z.string().min(1, "ກະລຸນາເລືອກຕໍາແໜ່ງ"),
  departmentId: z.string().optional(),
});

type SOPMappingFormValues = z.infer<typeof SOPMappingFormSchema>;

type SOPMappingFormProps = {
  initialValues?: Partial<SOPMappingFormValues>;
  onSubmit: (values: CreateSOPMappingDTO | UpdateSOPMappingDTO) => void;
  submitting?: boolean;
};

export function SOPMappingForm({
  initialValues,
  onSubmit,
  submitting,
}: SOPMappingFormProps) {
  const methods = RHF.useForm<SOPMappingFormValues>({
    resolver: zodResolver(SOPMappingFormSchema),
    defaultValues: {
      sopId: initialValues?.sopId ?? "",
      positionId: initialValues?.positionId ?? "",
      departmentId: initialValues?.departmentId ?? "",
    },
  });

  const positionId = RHF.useWatch({
    control: methods.control,
    name: "positionId",
  });
  const departmentId = RHF.useWatch({
    control: methods.control,
    name: "departmentId",
  });

  // When position is selected, clear and filter department
  useEffect(() => {
    if (positionId) {
      methods.setValue("departmentId", "");
    }
  }, [positionId, methods]);

  // // When department is selected, clear and filter position
  // useEffect(() => {
  //   if (departmentId) {
  //     methods.setValue("positionId", "");
  //   }
  // }, [departmentId, methods]);

  return (
    <FormRoot
      methods={methods}
      onSubmit={(values) => {
        onSubmit({
          sopId: values.sopId,
          positionId: values.positionId,
          departmentId: values.departmentId || undefined,
        });
      }}
    >
      <div className="space-y-4">
        {!initialValues?.sopId && (
          <FormInfiniteCombobox<SOPItem>
            name="sopId"
            label="ເລືອກ SOP"
            requiredMark
            queryKey={["sops", "mapping-form"]}
            queryFn={(args) =>
              fetchLookupForInfinite(`${config.apiUrl}/sop/lookup`, {
                search: args.search,
                pageParam: args.pageParam,
              })
            }
            preloadQueryFn={(id) =>
              hydrateLookupItem(`${config.apiUrl}/sop/lookup`, id)
            }
            getLabel={(item) => item.name}
            getValue={(item) => item.id}
            placeholder="ຄົ້ນຫາ SOP..."
          />
        )}

        <FormInfiniteCombobox<PositionItem>
          name="positionId"
          label="ເລືອກຕໍາແໜ່ງ"
          requiredMark
          queryKey={["positions", "mapping-form", departmentId || "all"]}
          queryFn={async (args) => {
            const url = `${config.apiUrl}/positions/lookup`;
            const limit = 20;
            const skip = (args.pageParam - 1) * limit;
            const u = new URL(url);
            if (args.search) u.searchParams.set("q", args.search);
            u.searchParams.set("limit", String(limit));
            u.searchParams.set("skip", String(skip));
            if (departmentId) {
              u.searchParams.set("departmentId", departmentId);
            }
            const data = await fetcher.get<{
              items: PositionItem[];
              total?: number;
            }>(u.toString());
            const items = Array.isArray(data.items) ? data.items : [];
            const total =
              typeof data.total === "number" ? data.total : items.length;
            const nextPage = skip + limit < total ? args.pageParam + 1 : null;
            return { items, nextPage };
          }}
          preloadQueryFn={(id) =>
            hydrateLookupItem(`${config.apiUrl}/positions/lookup`, id)
          }
          getLabel={(item) => (item as PositionItem).title || item.name}
          getValue={(item) => item.id}
          placeholder="ຄົ້ນຫາຕໍາແໜ່ງ..."
        />

        <FormInfiniteCombobox<DepartmentItem>
          name="departmentId"
          label="ເລືອກພະແນກ (ບໍ່ບັງຄັບ)"
          queryKey={["departments", "mapping-form", positionId || "all"]}
          queryFn={async (args) => {
            const url = `${config.apiUrl}/departments/lookup`;
            const limit = 20;
            const skip = (args.pageParam - 1) * limit;
            const u = new URL(url);
            if (args.search) u.searchParams.set("q", args.search);
            u.searchParams.set("limit", String(limit));
            u.searchParams.set("skip", String(skip));
            if (positionId) {
              u.searchParams.set("positionId", positionId);
            }
            const data = await fetcher.get<{
              items: DepartmentItem[];
              total?: number;
            }>(u.toString());
            const items = Array.isArray(data.items) ? data.items : [];
            const total =
              typeof data.total === "number" ? data.total : items.length;
            const nextPage = skip + limit < total ? args.pageParam + 1 : null;
            return { items, nextPage };
          }}
          preloadQueryFn={(id) =>
            hydrateLookupItem(`${config.apiUrl}/departments/lookup`, id)
          }
          getLabel={(item) => item.name}
          getValue={(item) => item.id}
          placeholder="ຄົ້ນຫາພະແນກ..."
          clearable
        />

        <div className="flex justify-end gap-2">
          <Button type="submit" disabled={submitting}>
            <Save className="mr-2 h-4 w-4" />
            {submitting ? "ກຳລັງບັນທຶກ..." : "ບັນທຶກ"}
          </Button>
        </div>
      </div>
    </FormRoot>
  );
}
